import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TargetsPerformance } from "@/components/dashboard/TargetsPerformance";
import { AccessRights } from "@/components/dashboard/AccessRights";
import { Building2, Link2, Loader2, CheckCircle2 } from "lucide-react";

const inputCls = "bg-[#0B0F17] border-[#334155] text-slate-100 placeholder:text-slate-500";

export const Settings = ({ onCompanyChange }) => {
  const [company, setCompany] = useState("");
  const [sheet, setSheet] = useState(null);
  const [sheetUrl, setSheetUrl] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/settings/company").then((r) => setCompany(r.data.name)).catch(() => {});
    api.get("/settings/sheet").then((r) => setSheet(r.data)).catch(() => {});
  }, []);

  const saveCompany = async (e) => {
    e.preventDefault();
    await api.post("/settings/company", { name: company });
    toast.success("Company name saved");
    onCompanyChange && onCompanyChange(company);
  };

  const connectSheet = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/settings/sheet", { url: sheetUrl });
      setSheet(data);
      toast.success(`Connected! ${data.showroom_rows} showroom rows · ${data.rto_rows} RTO rows`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div data-testid="company-settings" className="bg-[#1E293B] border border-[#334155] rounded-lg p-5">
          <h3 className="text-base font-semibold mb-1 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <Building2 className="h-4 w-4 text-blue-400" /> Company
          </h3>
          <p className="text-xs text-slate-400 mb-3">Shown in the top bar and on printed estimates.</p>
          <form onSubmit={saveCompany} className="flex gap-2">
            <Input data-testid="company-name-input" required value={company} onChange={(e) => setCompany(e.target.value)} className={inputCls} />
            <Button data-testid="save-company-btn" type="submit" className="bg-blue-600 hover:bg-blue-500 text-white">Save</Button>
          </form>
        </div>
        <div data-testid="sheet-settings" className="bg-[#1E293B] border border-[#334155] rounded-lg p-5">
          <h3 className="text-base font-semibold mb-1 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
            <Link2 className="h-4 w-4 text-emerald-400" /> Google Sheet
          </h3>
          <p className="text-xs text-slate-400 mb-3">Paste your sheet link (sharing: Anyone with link → Viewer). Showroom & RTO tabs are detected automatically.</p>
          {sheet?.showroom_tab && (
            <p data-testid="sheet-connected-status" className="text-xs text-emerald-400 mb-2 flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" /> Connected: "{sheet.showroom_tab}" + "{sheet.rto_tab}"
            </p>
          )}
          <form onSubmit={connectSheet} className="flex gap-2">
            <Input data-testid="sheet-url-input" required placeholder="https://docs.google.com/spreadsheets/d/…" value={sheetUrl}
              onChange={(e) => setSheetUrl(e.target.value)} className={inputCls} />
            <Button data-testid="connect-sheet-btn" type="submit" disabled={busy} className="bg-emerald-600 hover:bg-emerald-500 text-white shrink-0">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Connect"}
            </Button>
          </form>
        </div>
      </div>
      <AccessRights />
      <div>
        <h3 className="text-lg font-semibold mb-3" style={{ fontFamily: "Outfit, sans-serif" }}>Employees, Targets & Digest</h3>
        <TargetsPerformance isAdmin={true} />
      </div>
    </div>
  );
};
